import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Zap, ArrowRight, Youtube, Copy, Loader2, Wand2, Target, Hash, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NICHES = ["Gaming", "Tech", "Lifestyle", "Finance", "Education", "Fitness", "Food", "Travel", "Comedy", "Beauty"];

export default function Home() {
  const [niche, setNiche] = useState("");
  const [audience, setAudience] = useState("");
  const [count, setCount] = useState(5);
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!niche.trim() || !audience.trim()) {
      toast.error("Niche and target audience are required.");
      return;
    }
    setLoading(true);
    setIdeas([]);
    try {
      const { data } = await axios.post(`${API}/generate-ideas`, {
        niche: niche.trim(),
        audience: audience.trim(),
        count: Number(count),
        keywords: keywords.trim(),
      });
      setIdeas(data.ideas || []);
      toast.success(`Generated ${data.ideas.length} fresh ideas.`);
      setTimeout(() => {
        document.getElementById("results-section")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Generation failed.";
      toast.error(typeof msg === "string" ? msg : "Generation failed.");
    } finally {
      setLoading(false);
    }
  };

  const copyIdea = (idea) => {
    const text = `${idea.title}\n\nHook: ${idea.hook}\n\n${idea.description}`;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10 saturate-150"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="logo">
            <div className="w-9 h-9 bg-[#FF3B30] flex items-center justify-center">
              <Youtube className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-2xl tracking-tight leading-none uppercase">
              Idea<span className="text-[#FF3B30]">Forge</span>
            </div>
          </div>
          <Link
            data-testid="admin-link"
            to="/admin"
            className="font-sub text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors"
          >
            Admin →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF3B30] rounded-full blur-[160px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#FF3B30] rounded-full blur-[180px] opacity-50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 lg:pt-28 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 slide-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-white/[0.03] mb-8">
                <span className="w-1.5 h-1.5 bg-[#FF3B30] pulse-dot" />
                <span className="font-sub text-[0.7rem] uppercase tracking-[0.25em] text-white/70">
                  AI-Powered · 100% Free
                </span>
              </div>

              <h1
                data-testid="hero-title"
                className="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tighter uppercase leading-[0.85] mb-6"
              >
                Stop guessing.<br />
                Start <span className="text-[#FF3B30]">going viral.</span>
              </h1>

              <p className="font-body text-base sm:text-lg text-white/60 max-w-xl leading-relaxed mb-10">
                Generate scroll-stopping YouTube video ideas in seconds.
                Powered by GPT-5.2, tuned by ex-MrBeast strategists, free forever.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="#generator" className="btn-primary inline-flex items-center gap-2" data-testid="cta-generate">
                  <Wand2 className="w-4 h-4" />
                  Generate Ideas
                </a>
                <a href="#how" className="btn-secondary inline-flex items-center gap-2" data-testid="cta-how">
                  How it works
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 slide-up" style={{ animationDelay: "0.2s" }}>
              <div className="border border-white/10 bg-[#121212] p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF3B30]/10 blur-3xl" />
                <div className="relative">
                  <div className="font-sub text-[0.7rem] uppercase tracking-[0.25em] text-white/40 mb-4">
                    LIVE PREVIEW
                  </div>
                  <div className="font-display text-3xl leading-none uppercase mb-4">
                    "I tried <span className="text-[#FF3B30]">7 budget cameras</span> for 30 days"
                  </div>
                  <div className="font-body text-sm text-white/60 mb-6">
                    A documentary-style review with cliffhangers between cameras to maximize retention curve.
                  </div>
                  <div className="flex flex-wrap">
                    <span className="tag-chip">Tech</span>
                    <span className="tag-chip">Review</span>
                    <span className="tag-chip">Long-Form</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Generator */}
      <section id="generator" className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="font-sub text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-4">01 — Generator</div>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none tracking-tighter mb-6">
                Tell us<br />your <span className="text-[#FF3B30]">vibe.</span>
              </h2>
              <p className="font-body text-white/60 leading-relaxed">
                The more specific you get, the sharper the ideas. We synthesize your niche, audience and keywords
                into ten-thousand-foot creative direction.
              </p>
            </div>

            <form onSubmit={handleGenerate} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="generator-form">
              <div className="md:col-span-2">
                <label className="field-label flex items-center gap-2"><Target className="w-3 h-3" /> Niche / Category</label>
                <input
                  data-testid="input-niche"
                  className="field-input"
                  placeholder="e.g. Tech reviews, Gaming, Personal finance"
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  required
                />
                <div className="flex flex-wrap gap-2 mt-3">
                  {NICHES.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setNiche(n)}
                      data-testid={`niche-chip-${n.toLowerCase()}`}
                      className={`px-3 py-1 text-xs font-sub uppercase tracking-[0.15em] border transition-all ${
                        niche === n
                          ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                          : "bg-transparent border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="field-label flex items-center gap-2"><Users className="w-3 h-3" /> Target Audience</label>
                <input
                  data-testid="input-audience"
                  className="field-input"
                  placeholder="e.g. Gen Z gamers, busy parents"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="field-label flex items-center gap-2"><Sparkles className="w-3 h-3" /> Number of Ideas ({count})</label>
                <input
                  data-testid="input-count"
                  type="range"
                  min="1"
                  max="15"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  className="w-full accent-[#FF3B30] mt-3"
                />
                <div className="flex justify-between text-[0.65rem] text-white/40 font-sub tracking-[0.15em] mt-1">
                  <span>1</span><span>15</span>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="field-label flex items-center gap-2"><Hash className="w-3 h-3" /> Keywords (optional)</label>
                <input
                  data-testid="input-keywords"
                  className="field-input"
                  placeholder="e.g. iPhone 17, budget, comparison"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                />
              </div>

              <div className="md:col-span-2 flex flex-wrap gap-4 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="generate-btn"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Forging ideas...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" /> Generate Ideas
                    </>
                  )}
                </button>
                {ideas.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setIdeas([]); setNiche(""); setAudience(""); setKeywords(""); }}
                    className="btn-secondary"
                    data-testid="reset-btn"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Results */}
      <section id="results-section" className="border-b border-white/10">
        {loading && (
          <div className="max-w-7xl mx-auto px-6 py-20 text-center" data-testid="loading-state">
            <div className="inline-flex items-center gap-3 font-sub uppercase tracking-[0.25em] text-sm text-white/60">
              <Loader2 className="w-5 h-5 animate-spin text-[#FF3B30]" />
              GPT-5.2 is brainstorming...
            </div>
          </div>
        )}

        {!loading && ideas.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28" data-testid="results-grid">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <div>
                <div className="font-sub text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-3">02 — Results</div>
                <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none tracking-tighter">
                  Your <span className="text-[#FF3B30]">{ideas.length}</span> ideas
                </h2>
              </div>
              <div className="font-sub text-xs uppercase tracking-[0.2em] text-white/40">
                Click any card to copy
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ideas.map((idea, i) => (
                <div
                  key={i}
                  className="idea-card slide-up cursor-pointer group"
                  style={{ animationDelay: `${i * 0.08}s` }}
                  onClick={() => copyIdea(idea)}
                  data-testid={`idea-card-${i}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="font-display text-3xl text-[#FF3B30] leading-none">#{String(i + 1).padStart(2, "0")}</div>
                    <Copy className="w-4 h-4 text-white/30 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-sub font-bold text-xl text-white leading-snug mb-4">{idea.title}</h3>
                  <div className="border-l-2 border-[#FF3B30] pl-3 mb-4">
                    <div className="font-sub text-[0.65rem] uppercase tracking-[0.25em] text-white/40 mb-1">Hook</div>
                    <p className="font-body text-sm text-white/80 italic">"{idea.hook}"</p>
                  </div>
                  <p className="font-body text-sm text-white/55 leading-relaxed mb-4">{idea.description}</p>
                  <div className="flex flex-wrap">
                    {(idea.tags || []).map((t, ti) => (
                      <span key={ti} className="tag-chip">{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* How it Works */}
      <section id="how" className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="font-sub text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-4">03 — Workflow</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none tracking-tighter mb-16 max-w-2xl">
            Three steps. <span className="text-[#FF3B30]">Zero excuses.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Drop your niche", d: "Tell us what you create. Gaming, finance, beauty — anything goes." },
              { n: "02", t: "Define your audience", d: "Who watches you? The more specific, the sharper the ideas." },
              { n: "03", t: "Get viral concepts", d: "GPT-5.2 returns titles, hooks and angles ready to film today." },
            ].map((s) => (
              <div key={s.n} className="idea-card" data-testid={`step-${s.n}`}>
                <div className="font-display text-6xl text-[#FF3B30] leading-none mb-6">{s.n}</div>
                <div className="font-sub font-bold text-xl mb-3">{s.t}</div>
                <p className="font-body text-sm text-white/55 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="font-sub text-xs uppercase tracking-[0.25em] text-white/40" data-testid="footer-credit">
            © {new Date().getFullYear()} IdeaForge · Made by <span className="text-[#FF3B30] font-bold">BR</span> · Powered by GPT-5.2
          </div>
          <Link to="/admin" className="font-sub text-xs uppercase tracking-[0.25em] text-white/40 hover:text-white" data-testid="footer-admin-link">
            Admin Panel
          </Link>
        </div>
      </footer>
    </div>
  );
}
